import { Formik, FormikConfig } from 'formik';
import { Form } from 'react-router-dom';

import {
  DescriptiveInputWrapper,
  FormInput,
  InfoBox,
  LabeledStatus,
} from 'components/molecules';
import { useTranslation } from 'react-i18next';
import { II18nextPrefix } from 'types/components/components.interface';

import {
  useGetOwnerLogo,
  usePostOwnerLogo,
} from 'services/settings/individual/logo';
import {
  IOwnerLogoResponse,
  ISeetingIndividualResponse,
} from 'services/settings/individual/types';
import { ACTIVE_STATUS } from 'services/types';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { IFormProps } from '../types';

export const LicenseInitialValues: ISeetingIndividualResponse = {
  availableFrom: '',
  availableTo: '',
  owner: {
    firstName: '',
    lastName: '',
    fullName: '',
    name: '',
    email: {
      value: '',
    },
    phoneNumber: {
      prefix: '',
      number: '',
    },
    address: {
      street: '',
      houseNumber: '',
      flatNumber: '',
      zipCode: '',
      city: '',
      country: {
        polishName: '',
        englishName: '',
        codeAlfa2: '',
        codeAlfa3: '',
      },
    },
    accountDetails: {
      bankName: '',
      bankAccountNumber: '',
      swift: '',
    },
    taxOffice: {
      name: '',
      address: {
        street: '',
        houseNumber: '',
        flatNumber: '',
        zipCode: '',
        city: '',
        country: {
          polishName: '',
          englishName: '',
          codeAlfa2: '',
          codeAlfa3: '',
        },
      },
    },
    companyId: {
      companyIdentification: '',
      country: {
        polishName: '',
        englishName: '',
        codeAlfa2: '',
        codeAlfa3: '',
      },
    },
    logo: '',
  },
  availableServices: [
    {
      serviceName: 'string',
      available: true,
    },
  ],
  availableSettlementMethods: [
    {
      settlementMethodName: 'string',
      available: true,
    },
  ],
  defaultCurrency: { currencyCode: '', id: 0, name: '' },
  isDemo: false,
  demoMode: null,
  clientModuleAccess: false,
  isDetailedReportingEnabled: false,
};

const ownerLogoInitialValues: IOwnerLogoResponse = {
  showOwnersLogoAsMain: false,
  pictureId: '',
  pictureFileName: '',
  picturePath: '',
  pictureLink: '',
  picturePreviewPath: '',
  picturePreviewLink: '',
  pictureSize: 0,
  pictureExtension: '',
  pictureHash: '',
  pictureWidth: 0,
  pictureHeight: 0,
};

export interface IlicenseSettingsForm
  extends FormikConfig<ISeetingIndividualResponse>,
    IFormProps,
    II18nextPrefix {}

const LicenseSettingsForm = ({
  i18nextPrefix,

  topbarContent,
  tittle,
  status,
  initialValues = LicenseInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  ...props
}: IlicenseSettingsForm) => {
  const { t } = useTranslation();

  const ownerLogo = useGetOwnerLogo();
  const updateOwnerLogo = usePostOwnerLogo();

  const handleMailClick = (e: any) => {
    e.preventDefault();
    const email = 'example@example.com';
    const subject = 'Wiadomość z aplikacji';
    const body = 'Dzień dobry,\n\nChciałbym zapytać o...';

    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    window.location.href = mailtoLink;
  };

  return (
    <div className='flex flex-col w-full h-full gap-4 '>
      <div className='flex flex-col '>
        <Formik
          enableReinitialize
          initialValues={ownerLogo.data ?? ownerLogoInitialValues}
          onSubmit={() => {}}>
          {({ values }) => (
            <Form>
              <DescriptiveInputWrapper header={t('setting.license.logo')}>
                <InfoBox.Image
                  fullResolutionUrl={values.pictureLink}
                  previewUrl={values.picturePreviewLink}
                  accept='image/jpg, image/jpeg, image/png'
                  editable={true}
                  onSave={async (file, actions) =>
                    await updateOwnerLogo.mutateAsync(file, {
                      onSuccess: async () => {
                        await ownerLogo.refetch();
                        actions.close();
                      },
                      onError: error => onErrorShowAllErrorsToast(error),
                    })
                  }
                />
              </DescriptiveInputWrapper>
            </Form>
          )}
        </Formik>
      </div>
      <Formik
        {...props}
        validateOnChange
        validateOnBlur
        validateOnMount
        initialStatus={status}
        enableReinitialize
        initialValues={initialValues}>
        {({ setValues, values, status, errors, setFieldValue }) => (
          <Form className='flex w-full h-full flex-col xl:flex-row gap-[30px]'>
            <div className='flex flex-col gap-[30px] w-[330px]'>
              <DescriptiveInputWrapper
                header={t('setting.license.contactDetails')}>
                <FormInput.Text
                  name='owner.firstName'
                  label={t('input.label.firstName')}
                />
                <FormInput.Text
                  name='owner.lastName'
                  label={t('input.label.lastName')}
                />
                <FormInput.Text
                  name='owner.fullName'
                  label={t('input.label.fullName')}
                />
                <FormInput.Text
                  name='owner.name'
                  label={t('input.label.name')}
                />
                <FormInput.Text
                  name='owner.email.value'
                  label={t('input.label.email')}
                />
                <FormInput.Text
                  name='owner.phoneNumber.prefix'
                  label={t('input.label.phoneNumber')}
                />
                <FormInput.Text
                  name='owner.phoneNumber.number'
                  label={t('input.label.phoneNumber')}
                />
              </DescriptiveInputWrapper>

              <DescriptiveInputWrapper header={t('setting.license.company')}>
                <FormInput.Text
                  name='owner.companyId.companyIdentification'
                  label={t('input.label.companyId')}
                />
                <FormInput.Text
                  name='owner.companyId.country.polishName'
                  label={t('setting.license.inputs.polishName')}
                />
                <FormInput.Text
                  name='owner.companyId.country.englishName'
                  label={t('setting.license.inputs.englishName')}
                />
                <FormInput.Text
                  name='owner.companyId.country.codeAlfa2'
                  label={t('setting.license.inputs.codeAlfa2')}
                />
                <FormInput.Text
                  name='owner.companyId.country.codeAlfa3'
                  label={t('setting.license.inputs.codeAlfa3')}
                />
              </DescriptiveInputWrapper>
            </div>
            <div className='flex flex-col gap-[30px] w-[330px]'>
              <DescriptiveInputWrapper header={t('setting.license.address')}>
                <FormInput.Text
                  name='owner.address.street'
                  label={t('input.label.street')}
                />
                <FormInput.Text
                  name='owner.address.houseNumber'
                  label={t('input.label.houseNumber')}
                />
                <FormInput.Text
                  name='owner.address.flatNumber'
                  label={t('input.label.flatNumber')}
                />
                <FormInput.Text
                  name='owner.address.zipCode'
                  label={t('input.label.zipCode')}
                />
                <FormInput.Text
                  name='owner.address.city'
                  label={t('input.label.city')}
                />
                <FormInput.Text
                  name='owner.address.country.polishName'
                  label={t('setting.license.inputs.polishName')}
                />
                <FormInput.Text
                  name='owner.address.country.englishName'
                  label={t('setting.license.inputs.englishName')}
                />
                <FormInput.Text
                  name='owner.address.country.codeAlfa2'
                  label={t('setting.license.inputs.codeAlfa2')}
                />
                <FormInput.Text
                  name='owner.address.country.codeAlfa3'
                  label={t('setting.license.inputs.codeAlfa3')}
                />
              </DescriptiveInputWrapper>

              <DescriptiveInputWrapper
                header={t('setting.license.AccountDetails')}>
                <FormInput.Text
                  name='owner.accountDetails.bankName'
                  label={t('input.label.bankName')}
                />
                <FormInput.Text
                  name='owner.accountDetails.bankAccountNumber'
                  label={t('input.label.bankAccountNumber')}
                />
                <FormInput.Text
                  name='owner.accountDetails.swift'
                  label={t('setting.license.inputs.swift')}
                />
              </DescriptiveInputWrapper>

              {values.availableServices && (
                <DescriptiveInputWrapper
                  header={t('setting.license.availableServices')}>
                  {values.availableServices.map((item, key) => (
                    <div className='flex w-full justify-between'>
                      <p> {item.serviceName}</p>
                      <LabeledStatus.Active
                        status={
                          item.available
                            ? ACTIVE_STATUS.Active
                            : ACTIVE_STATUS.Inactive
                        }
                      />
                    </div>
                  ))}{' '}
                </DescriptiveInputWrapper>
              )}
            </div>
            <div className='flex flex-col gap-[30px] w-[330px]'>
              <DescriptiveInputWrapper header={t('setting.license.TaxOffice')}>
                <FormInput.Text
                  name='owner.taxOffice.name'
                  label={t('input.label.name')}
                />
                <FormInput.Text
                  name='owner.taxOffice.address.street'
                  label={t('input.label.street')}
                />
                <FormInput.Text
                  name='owner.taxOffice.address.houseNumber'
                  label={t('input.label.houseNumber')}
                />
                <FormInput.Text
                  name='owner.taxOffice.address.flatNumber'
                  label={t('input.label.flatNumber')}
                />
                <FormInput.Text
                  name='owner.taxOffice.address.zipCode'
                  label={t('input.label.zipCode')}
                />
                <FormInput.Text
                  name='owner.taxOffice.address.city'
                  label={t('input.label.city')}
                />
                <FormInput.Text
                  name='owner.taxOffice.address.country.polishName'
                  label={t('setting.license.inputs.polishName')}
                />
                <FormInput.Text
                  name='owner.taxOffice.address.country.englishName'
                  label={t('setting.license.inputs.englishName')}
                />
                <FormInput.Text
                  name='owner.taxOffice.address.country.codeAlfa2'
                  label={t('setting.license.inputs.codeAlfa2')}
                />
                <FormInput.Text
                  name='owner.taxOffice.address.country.codeAlfa3'
                  label={t('setting.license.inputs.codeAlfa3')}
                />
              </DescriptiveInputWrapper>

              <DescriptiveInputWrapper header={t('setting.license.available')}>
                <FormInput.Text
                  type='datetime-local'
                  name='availableFrom'
                  label={t('input.label.availableFrom')}
                />
                {values.availableTo !== null && (
                  <FormInput.Text
                    type='datetime-local'
                    name='availableTo'
                    label={t('input.label.availableTo')}
                  />
                )}
              </DescriptiveInputWrapper>

              {values.availableSettlementMethods && (
                <DescriptiveInputWrapper
                  header={t('setting.license.availableSettlementMethods')}>
                  {values.availableSettlementMethods.map((item, key) => (
                    <div className='flex w-full justify-between'>
                      <p> {item.settlementMethodName}</p>
                      <LabeledStatus.Active
                        status={
                          item.available
                            ? ACTIVE_STATUS.Active
                            : ACTIVE_STATUS.Inactive
                        }
                      />
                    </div>
                  ))}
                </DescriptiveInputWrapper>
              )}
            </div>

            {/* <div className='buttons z-[2]'>

              <MagellanPopups.PopupChannelCreateForm
                clearPopupValues={() => console.log()}
                header={t('setting.license.contactDetails')}
                trigger={
                  <Button type='button' variant={BUTTON_VARIANT.submit}>
                    <Button.Icon
                      icon={<PiEnvelope transform='scale(-1,1)' />}
                    />
                    <Button.Text
                      text={t(`enum.TEMPLATE_TYPE_SETTINGS.write`)}
                    />
                  </Button>
                }
                showButtons={false}>
                <div className='flex w-full justify-between'>
                  <p> Email:</p>
                  <p>example@wp.pl</p>
                </div>
                <Button
                  type='button'
                  variant={BUTTON_VARIANT.submit}
                  onClick={e => handleMailClick(e)}>
                  <Button.Icon icon={<PiEnvelope transform='scale(-1,1)' />} />
                  <Button.Text text={t(`enum.TEMPLATE_TYPE_SETTINGS.write`)} />
                </Button>
              </MagellanPopups.PopupChannelCreateForm>
            </div> */}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default LicenseSettingsForm;
